// Neu (Rückmeldung 01.10.: „fix search bar to also search for tools and functions and pages and ai
// asking“) — die Befehlssuche der Kopfleiste. Geöffnet mit dem Suchknopf, F oder Strg/⌘+K (Shell)
// und am Telefon mit „Suchen“ in der Reiterleiste. EIN Feld, drei Gruppen:
//   Seiten & Werkzeuge   das Verzeichnis aus ./befehle (nach Rechten und Verfahren gefiltert)
//   Codes & Text         ein Code → /r/{code} (der Auflöser wie bei jedem Permalink); Freitext →
//                        „In Antworten suchen“ = die Fragebar der Auswertung, vorbelegt
//   KI-Assistent         „KI fragen: …“ → das Chatfenster mit der Frage, gestellt (chatStore
//                        `oeffneMitFrage`, derselbe Weg wie „✦ Fragen“ in der Fragebar)
// Tastatur: ↑/↓ (gesperrte Zeilen werden übersprungen), Enter, Esc; Tab bleibt im Dialog.
// ARIA: Feld = combobox, Liste = listbox mit Gruppen, die aktive Zeile per aria-activedescendant.
//
// Die Suchzeile bleibt im Zustand dieses Bausteins — nie im Browserspeicher (S1), nie in einer
// Adresse (S7), nie in einem Protokoll (E13: eine Frage an die KI geht nur an den lokalen Assistenten).
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";
import type { Ich } from "../../api/typen";
import { Icon, IconBtn, portalRoot } from "../ui";
import { useTheme } from "../../lib/theme";
import { TX } from "../../lib/texte";
import { useAuthStore } from "../../store/useAuthStore";
import { ebeneAnmelden, useStore } from "../../store/useStore";
import { befehleFuer, istGesperrt, naechste, vorwahl, zeilenFuer, zielVon, type Befehl, type Zeile } from "./befehle";
import { fragebarDa, fragebarVorbelegen } from "./navigation";
import "../../styles/bs-befehlssuche.css";

/* ------------------------------------------------------------------ offen/zu */

interface BefehlssucheZustand {
  offen: boolean;
  oeffnen(): void;
  schliessen(): void;
  umschalten(): void;
}

/** Offen oder zu — ein eigener kleiner Speicher, damit Kopfleiste, Reiterleiste und Shell (F, Strg+K) dieselbe Suche öffnen. */
export const useBefehlssuche = create<BefehlssucheZustand>((set) => ({
  offen: false,
  oeffnen: () => set({ offen: true }),
  schliessen: () => set({ offen: false }),
  umschalten: () => set((s) => ({ offen: !s.offen })),
}));

export function oeffneBefehlssuche(): void {
  useBefehlssuche.getState().oeffnen();
}

/** Der Chat wird wie in useAuthStore DYNAMISCH geholt: er ist ein eigener Baustein (nur mit Recht „assistent“). */
const chat = () => import("../../screens/ki/chatStore");

const FOKUSSIERBAR = 'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Auf diesen Wegen hängt ein Exportdialog (Auswertung, Exporte) — dort öffnet er sich ohne Wegwechsel. */
const MIT_EXPORTDIALOG = /^\/v\/[^/]+\/(auswerten|exporte)\/?$/;

/* ------------------------------------------------------------------ Baustein */

export function Befehlssuche(props: { V: string | null; ich: Ich | undefined; tastenkuerzel: () => void }) {
  const offen = useBefehlssuche((s) => s.offen);
  // Ein Wegwechsel von außen (Zurück im Browser) schließt — sonst stünde die Suche über dem neuen Bildschirm.
  const { pathname } = useLocation();
  const letzterWeg = useRef(pathname);
  useEffect(() => {
    if (letzterWeg.current === pathname) return;
    letzterWeg.current = pathname;
    useBefehlssuche.getState().schliessen();
  }, [pathname]);
  // Verschwindet die Schale (ein 401 führt zur Anmeldung), geht die Suche mit — sonst stünde sie
  // nach dem Anmelden sofort wieder offen über der Startseite (Review).
  useEffect(() => () => useBefehlssuche.getState().schliessen(), []);
  // Eigener innerer Baustein: jedes Öffnen beginnt mit leerer Zeile und frischer Vorwahl.
  return offen ? <BefehlssucheBlatt {...props} /> : null;
}

function BefehlssucheBlatt({ V, ich, tastenkuerzel }: { V: string | null; ich: Ich | undefined; tastenkuerzel: () => void }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const schliessen = useBefehlssuche((s) => s.schliessen);
  const { thema, umschalten } = useTheme();
  const setzeVerfahren = useStore((s) => s.setzeVerfahren);
  const setzeExportDialog = useStore((s) => s.setzeExportDialog);
  const abmelden = useAuthStore((s) => s.abmelden);
  const id = useId();
  const feldRef = useRef<HTMLInputElement>(null);
  const blattRef = useRef<HTMLDivElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState("");
  const [an, setAn] = useState(-1);
  const [handverlesen, setHandverlesen] = useState(false);

  const befehle = useMemo(() => befehleFuer({ V, ich, dunkel: thema === "dark" }), [V, ich, thema]);
  const zeilen = useMemo(() => zeilenFuer(befehle, q, { V, ich }), [befehle, q, V, ich]);
  const aktiv = handverlesen && an >= 0 && an < zeilen.length && !istGesperrt(zeilen[an]) ? an : vorwahl(q, zeilen);
  const zeileId = (i: number) => `${id}-z${i}`;

  // Öffnen: Fokus ins Feld (nicht ins Blatt wie beim Modal — hier IST das Feld der Dialog), die
  // Seite darunter rollt nicht mit, Esc über den Ebenen-Stapel; Schließen gibt den Fokus zurück.
  useEffect(() => {
    const vorher = document.activeElement;
    feldRef.current?.focus({ preventScroll: true });
    const alt = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const weg = ebeneAnmelden("befehlssuche", () => useBefehlssuche.getState().schliessen());
    return () => {
      weg();
      document.body.style.overflow = alt;
      if (vorher instanceof HTMLElement && vorher !== document.body) vorher.focus({ preventScroll: true });
    };
  }, []);

  // Neue Suchzeile → Liste von oben; die aktive Zeile bleibt sichtbar, wenn die Pfeiltasten über den Rand wandern.
  useEffect(() => {
    if (listeRef.current) listeRef.current.scrollTop = 0;
  }, [q]);
  useEffect(() => {
    if (aktiv < 0 || !handverlesen) return;
    document.getElementById(zeileId(aktiv))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktiv, handverlesen]);

  const befehlAusfuehren = (b: Befehl) => {
    if (b.verfahren) setzeVerfahren(b.verfahren);
    if (b.zu) {
      // Ein Reiter der Auswertung, auf der man steht: nur der Reiter wechselt, die Filter bleiben.
      const ziel = zielVon(b.zu, { pathname, search });
      navigate(ziel.zu, { replace: ziel.ersetzen });
      return;
    }
    switch (b.aktion) {
      case "thema":
        umschalten();
        break;
      case "tastenkuerzel":
        tastenkuerzel();
        break;
      case "abmelden":
        void abmelden().then(() => navigate("/anmelden", { replace: true }));
        break;
      case "auftraege":
        // Das Panel gehört dem Aufträge-Knopf der Kopfleiste (eigener Zustand) — ihn drücken,
        // sobald die Suche weg ist.
        requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(".af-knopf")?.click());
        break;
      case "ki":
        void chat().then((m) => m.setzeOffen(true));
        break;
      case "rohdaten":
        if (!V) break;
        setzeExportDialog({ quelle: "verfahren" });
        if (!MIT_EXPORTDIALOG.test(pathname)) navigate(`/v/${encodeURIComponent(V)}/exporte`);
        break;
    }
  };

  const ausfuehren = (z: Zeile | undefined) => {
    if (!z || istGesperrt(z)) return;
    schliessen();
    if (z.art === "befehl") befehlAusfuehren(z.befehl);
    else if (z.art === "code") navigate("/r/" + encodeURIComponent(z.code));
    else if (z.art === "antworten" && V) {
      fragebarVorbelegen(z.text);
      // Steht die Fragebar schon im Bild, bleibt die Ansicht (mit ihren Filtern) stehen.
      if (!fragebarDa()) navigate(`/v/${encodeURIComponent(V)}/auswerten`);
    } else if (z.art === "ki" && V) {
      const frage = z.text;
      void chat().then((m) => m.oeffneMitFrage(V, frage));
    }
  };

  const tasten = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // Enter beendet erst die Eingabehilfe (Diktat, IME)
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const i = naechste(zeilen, aktiv, e.key === "ArrowDown" ? 1 : -1);
      if (i >= 0) {
        setHandverlesen(true);
        setAn(i);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      ausfuehren(aktiv >= 0 ? zeilen[aktiv] : undefined);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      schliessen();
    }
  };

  // Tab-Falle wie im Modal: der Fokus bleibt im Dialog.
  const falle = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !blattRef.current) return;
    const ziele = Array.from(blattRef.current.querySelectorAll<HTMLElement>(FOKUSSIERBAR));
    const erstes = ziele[0];
    const letztes = ziele[ziele.length - 1];
    if (!erstes || !letztes) return;
    if (e.shiftKey && (document.activeElement === erstes || document.activeElement === blattRef.current)) {
      e.preventDefault();
      letztes.focus();
    } else if (!e.shiftKey && document.activeElement === letztes) {
      e.preventDefault();
      erstes.focus();
    }
  };

  // Gruppen in Anzeigereihenfolge, jede Zeile mit ihrem Index in `zeilen`.
  const gruppen: { titel: string; eintraege: { z: Zeile; i: number }[] }[] = [];
  zeilen.forEach((z, i) => {
    const titel = z.art === "befehl" ? TX.befehle.gruppeSeiten : z.art === "ki" ? TX.befehle.gruppeKi : TX.befehle.gruppeCodes;
    const letzte = gruppen[gruppen.length - 1];
    if (letzte?.titel === titel) letzte.eintraege.push({ z, i });
    else gruppen.push({ titel, eintraege: [{ z, i }] });
  });
  const keineSeite = q.trim() !== "" && !zeilen.some((z) => z.art === "befehl");
  const listeId = `${id}-liste`;

  return createPortal(
    <div className="modal-wrap bs-hof anim-fade">
      <div className="bs-schleier" onClick={schliessen} aria-hidden="true" />
      <div ref={blattRef} className="bs-blatt glass-strong anim-pop" role="dialog" aria-modal="true" aria-label={TX.befehle.titel} tabIndex={-1} onKeyDown={falle}>
        <div className="bs-kopf">
          <Icon name="magnifying-glass" size={18} />
          <label htmlFor={`${id}-feld`} className="sr">
            {TX.befehle.feld}
          </label>
          <input
            ref={feldRef}
            id={`${id}-feld`}
            className="bs-feld"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setHandverlesen(false);
            }}
            onKeyDown={tasten}
            placeholder={TX.befehle.platzhalter}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            maxLength={200}
            role="combobox"
            aria-expanded="true"
            aria-controls={listeId}
            aria-autocomplete="list"
            aria-activedescendant={aktiv >= 0 ? zeileId(aktiv) : undefined}
          />
          <IconBtn type="button" icon="x" size={16} box={34} title={TX.allgemein.schliessen} onClick={schliessen} />
        </div>
        {keineSeite && <p className="bs-leer">{TX.befehle.keineTreffer}</p>}
        <div ref={listeRef} className="bs-liste scroll" role="listbox" id={listeId} aria-label={TX.befehle.liste}>
          {gruppen.map((g, gi) => (
            <div key={g.titel} role="group" aria-labelledby={`${id}-g${gi}`}>
              <div id={`${id}-g${gi}`} className="bs-titel" role="presentation">
                {g.titel}
              </div>
              {g.eintraege.map(({ z, i }) => (
                <ZeileAnsicht
                  key={schluessel(z)}
                  z={z}
                  id={zeileId(i)}
                  an={i === aktiv}
                  beiZeiger={() => {
                    if (i !== aktiv && !istGesperrt(z)) {
                      setHandverlesen(true);
                      setAn(i);
                    }
                  }}
                  beiWahl={() => ausfuehren(z)}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="bs-fuss" aria-hidden="true">
          {TX.befehle.hilfe}
        </div>
      </div>
    </div>,
    portalRoot(),
  );
}

function schluessel(z: Zeile): string {
  return z.art === "befehl" ? z.befehl.id : z.art;
}

function ZeileAnsicht({ z, id, an, beiZeiger, beiWahl }: { z: Zeile; id: string; an: boolean; beiZeiger: () => void; beiWahl: () => void }) {
  const gesperrt = istGesperrt(z);
  const grund = (z.art === "antworten" || z.art === "ki") && z.gesperrt ? z.gesperrt : null;
  const [icon, titel, ort, mono] =
    z.art === "befehl"
      ? [z.befehl.icon, z.befehl.titel, z.befehl.ort, false]
      : z.art === "code"
        ? ["arrow-square-out", TX.befehle.oeffnen(z.code), TX.befehle.codeArt, true]
        : z.art === "antworten"
          ? ["magnifying-glass", TX.befehle.inAntworten(z.text), TX.befehle.inAntwortenArt, false]
          : ["sparkle", TX.befehle.kiFragen(z.text), TX.befehle.kiArt, false];
  return (
    <div
      id={id}
      role="option"
      aria-selected={an}
      aria-disabled={gesperrt || undefined}
      className={`bs-zeile${an ? " an" : ""}${gesperrt ? " aus" : ""}`}
      onMouseMove={beiZeiger}
      onMouseDown={(e) => e.preventDefault()}
      onClick={beiWahl}
    >
      <span className="bs-ico" aria-hidden="true">
        <Icon name={icon} size={17} weight={z.art === "ki" ? "fill" : "regular"} />
      </span>
      <span className="bs-text">
        <span className={`bs-name${mono ? " mono" : ""}`} translate={mono ? "no" : undefined}>
          {titel}
        </span>
        {grund && <span className="bs-grund">{grund}</span>}
      </span>
      {!grund && <span className="bs-ort">{ort}</span>}
    </div>
  );
}
